import React, { useState } from 'react';

import { showToast } from '@opendoor/bricks/complex/Toast';
import {
  Button,
  DropdownSingleSelect,
  Flex,
  IDropdownSingleSelectProps,
} from '@opendoor/bricks/core';
import { Sentry } from '@opendoor/observability';

import { OdProtosListingsData_ExperiencePartnerSupportRequestType as SupportRequestType } from '../../__generated__/athenaGlobalTypes';
import { useRequestExperiencePartnerSupport } from '../../graphql/athena/mutations/RequestExperiencePartnerSupport';

type SupportRequestFormProps = {
  lwcProductId: string;
};

interface SupportRequestOption {
  label: string;
  value: SupportRequestType;
}

/** Support request types */
export const SUPPORT_REQUEST_OPTIONS: SupportRequestOption[] = [
  {
    label: 'OD Offer Questions',
    value: SupportRequestType.SUPPORT_REQUEST_TYPE_OFFER_EXPLORATION,
  },
  {
    label: 'Repair Ask Breakdown',
    value: SupportRequestType.SUPPORT_REQUEST_TYPE_REPAIR_ASKS,
  },
  {
    label: 'Unresponsive Customer',
    value: SupportRequestType.SUPPORT_REQUEST_TYPE_SELLER_CONTACT,
  },
];

/** Submits an experience partner support request*/
export const SupportRequestForm: React.FC<SupportRequestFormProps> = ({ lwcProductId }) => {
  const [showForm, setShowForm] = useState(false);
  const [selectedOption, setSelectedOption] = useState('');
  const { requestExperiencePartnerSupport, error } = useRequestExperiencePartnerSupport();

  const onSubmit = (_: React.MouseEvent) => {
    if (
      window.confirm(
        'This will notify the Experience Partner that you or the seller needs support. Note they will receive this immediately and take action with 24 hour. There is no need to follow up with an email.',
      )
    ) {
      requestExperiencePartnerSupport(lwcProductId, selectedOption as SupportRequestType)
        .then(() => {
          showToast('info', `Successfully submitted support request`, 'lwc-dashboard-toast}', {
            toastOptions: { autoClose: 3000 },
          });
        })
        .catch((e) => {
          Sentry.withScope((scope) => {
            scope.setExtra('lwc_product_id', lwcProductId);
            scope.setExtra('support_request_type', selectedOption);
            scope.setExtra('error', JSON.stringify(e));
            Sentry.captureException(e);
          });
        });
    }
  };

  const handleOptionChange: IDropdownSingleSelectProps['onChange'] = (value) => {
    if (!value) {
      return;
    }
    setSelectedOption(value.toString());
  };

  if (error) {
    showToast('error', 'Error submitting support request', 'lwc-dashboard-toast', {
      toastOptions: { autoClose: false },
    });
  }

  return (
    <Flex flexDirection="column">
      {showForm ? (
        <>
          <DropdownSingleSelect
            aria-labelledby=""
            autoComplete="off"
            title="Type of support request:"
            placeholder="Select an option"
            selectedValue={selectedOption ? [selectedOption] : []}
            onChange={handleOptionChange}
            options={SUPPORT_REQUEST_OPTIONS}
          />
          <Button
            my={3}
            aria-label="Submit experience partner support request"
            analyticsName="submit-support-request"
            onClick={onSubmit}
            disabled={!selectedOption}
          >
            Request support from experience partner
          </Button>
        </>
      ) : (
        <Button
          aria-label="Show support request form"
          analyticsName="show-support-request-form"
          onClick={() => {
            setShowForm(true);
          }}
          variant="secondary"
        >
          Request support from experience partner
        </Button>
      )}
    </Flex>
  );
};
